import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { GraphPathLookupParameters } from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { isPathLookupGraphMetadata } from 'components/Graph/GraphState';
import { loadGraph } from 'components/Graph/commands/loadGraph';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikProps, useFormik } from 'formik';
import { flow, pipe } from 'fp-ts/lib/function';
import { useEffect, useState } from 'react';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import {
  type HostToDefaultGWFormValues,
  hostToDefaultGWFormValuesToParameters,
  hostToDefaultGWParametersToFormValues,
} from './HostToDefaultGWFormValues';
import {
  type MulticastFormValues,
  multicastFormValuesToParameters,
  multicastParametersToFormValues,
} from './MulticastFormValues';
import {
  advancedOptionsInitialValues,
  advancedOptionsToFormValues,
} from './PathLookupAdvancedOptionsFormValues';
import {
  getPathLookupAdvancedOptionsPresetIdFromLocalStorage,
  getPathLookupHostToDefaultGWFromLocalStorage,
  getPathLookupMulticastFromLocalStorage,
  getPathLookupUnicastFromLocalStorage,
} from './PathLookupLocalStorage';
import {
  type UnicastFormValues,
  unicastFormValuesToParameters,
  unicastParametersToFormValues,
} from './UnicastFormValues';
import { isMulticastAddress } from './helpers/isMulticastAddress';
import { validateHostToDefaultGW } from './validateHostToDefaultGW';
import { validateMulticast } from './validateMulticast';
import { validateUnicast } from './validateUnicast';

export interface PathLookupDataInputHook {
  hostToDefaultGWFormik: FormikProps<HostToDefaultGWFormValues>;
  multicastFormik: FormikProps<MulticastFormValues>;
  unicastFormik: FormikProps<UnicastFormValues>;
  resetAdvancedOptionsToSelectedPreset: () => void;
}

export const multicastFormInitialValues: MulticastFormValues = {
  group: '',
  receiver: '',
  securedPath: true,
  source: '',
  vrf: null,
  firstHopAlgorithm: 'automatic',
  entryPoints: [{ host: null, iface: null }],
  ...advancedOptionsInitialValues,
};

export const unicastFormInitialValues: UnicastFormValues = {
  destinationPoint: '',
  hostMode: true,
  securedPath: true,
  startingPoint: '',
  vrf: null,
  firstHopAlgorithm: 'automatic',
  entryPoints: [{ host: null, iface: null }],
  ...advancedOptionsInitialValues,
};

export const hostToDefaultGWFormInitialValues: HostToDefaultGWFormValues = {
  startingPoint: '',
  vrf: null,
};

const getUnicastInitialValues = (): UnicastFormValues =>
  pipe(
    getPathLookupUnicastFromLocalStorage(),
    nullable.fold(
      () => unicastFormInitialValues,
      unicastParametersToFormValues,
    ),
  );

const getMulticastInitialValues = (): MulticastFormValues =>
  pipe(
    getPathLookupMulticastFromLocalStorage(),
    nullable.fold(
      () => multicastFormInitialValues,
      multicastParametersToFormValues,
    ),
  );

const getHostToDefaultGWInitialValues = (): HostToDefaultGWFormValues =>
  pipe(
    getPathLookupHostToDefaultGWFromLocalStorage(),
    nullable.fold(
      () => hostToDefaultGWFormInitialValues,
      hostToDefaultGWParametersToFormValues,
    ),
  );

export const usePathLookupDataInput = (): PathLookupDataInputHook => {
  const msg = useLocale();
  // Initial values are loaded from local storage intentionally only upon compoment mount.
  // We don't want to reinitialize the form when local storage entry changes e.g. from different tab.
  const [unicastInitialValues, setUnicastInitialValues] =
    useState<UnicastFormValues>(getUnicastInitialValues);
  const [multicastInitialValues, setMulticastInitialValues] =
    useState<MulticastFormValues>(getMulticastInitialValues);
  const [hostToDefaultGWInitialValues, setHostTODefaultGWInitialValues] =
    useState<HostToDefaultGWFormValues>(getHostToDefaultGWInitialValues);
  const [appliedAdvancedOptionsPresetId, setAppliedAdvancedOptionsPresetId] =
    useState<string | null>(
      getPathLookupAdvancedOptionsPresetIdFromLocalStorage,
    );

  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    pathLookUpMetadata: (s) =>
      isPathLookupGraphMetadata(s.graphMetadata) ? s.graphMetadata : null,
    selectedAdvancedOptions: prop('pathlookup', 'selectedAdvancedOptions'),
  });

  const submitRequest = async (parameters: GraphPathLookupParameters) => {
    await ctxProps.dispatch(loadGraph, {
      overlay: 'none',
      parameters,
    });
  };

  const submitUnicast = flow(unicastFormValuesToParameters, submitRequest);

  const submitMulticast = flow(multicastFormValuesToParameters, submitRequest);

  const submitHostToDefaultGW = flow(
    hostToDefaultGWFormValuesToParameters,
    submitRequest,
  );

  const handleSwitchToMulticast = useStableCallback(
    (values: UnicastFormValues) => {
      const multicastValues: MulticastFormValues = {
        applications: values.applications,
        applicationsMatchWithRegex: values.applicationsMatchWithRegex,
        category: values.category,
        enableRegions: values.enableRegions,
        flags: values.flags,
        group: values.destinationPoint,
        dstPorts: values.dstPorts,
        dstRegions: values.dstRegions,
        protocol: values.protocol,
        receiver: '',
        securedPath: values.securedPath,
        source: values.startingPoint,
        srcPorts: values.srcPorts,
        srcRegions: values.srcRegions,
        tracked: values.tracked,
        fragmentOffset: values.fragmentOffset,
        firstHopAlgorithm: values.firstHopAlgorithm,
        ttl: values.ttl,
        vrf: values.vrf,
        icmpOptions: values.icmpOptions,
        entryPoints: values.entryPoints,
        url: values.url,
      };
      setMulticastInitialValues(multicastValues);
      ctxProps.dispatch(showSidePanel, {
        pathLookupDataInputTabId: 'multicast',
      });
      submitMulticast(multicastValues);
    },
  );

  const handleSwitchToUnicast = useStableCallback(
    (values: MulticastFormValues) => {
      const unicastValues: UnicastFormValues = {
        applications: values.applications,
        applicationsMatchWithRegex: values.applicationsMatchWithRegex,
        category: values.category,
        enableRegions: values.enableRegions,
        flags: values.flags,
        destinationPoint: values.group,
        dstPorts: values.dstPorts,
        dstRegions: values.dstRegions,
        protocol: values.protocol,
        securedPath: values.securedPath,
        startingPoint: values.source,
        srcPorts: values.srcPorts,
        srcRegions: values.srcRegions,
        vrf: values.vrf,
        hostMode: true,
        tracked: values.tracked,
        fragmentOffset: values.fragmentOffset,
        firstHopAlgorithm: values.firstHopAlgorithm,
        ttl: values.ttl,
        icmpOptions: values.icmpOptions,
        entryPoints: values.entryPoints,
        url: values.url,
      };
      setUnicastInitialValues(unicastValues);
      ctxProps.dispatch(showSidePanel, { pathLookupDataInputTabId: 'unicast' });
      submitUnicast(unicastValues);
    },
  );

  const multicastFormik = useFormik({
    enableReinitialize: true,
    initialValues: multicastInitialValues,
    validate: validateMulticast(msg),
    onSubmit: makeFormikOnSubmit((values: MulticastFormValues) => {
      if (!isMulticastAddress(values.group)) {
        handleSwitchToUnicast(values);
      } else {
        submitMulticast(values);
      }
    }),
  });

  const unicastFormik = useFormik<UnicastFormValues>({
    enableReinitialize: true,
    initialValues: unicastInitialValues,
    validate: validateUnicast(msg),
    onSubmit: makeFormikOnSubmit((values: UnicastFormValues) => {
      if (isMulticastAddress(values.destinationPoint)) {
        handleSwitchToMulticast(values);
      } else {
        submitUnicast(values);
      }
    }),
  });

  const hostToDefaultGWFormik = useFormik<HostToDefaultGWFormValues>({
    enableReinitialize: true,
    initialValues: hostToDefaultGWInitialValues,
    validate: validateHostToDefaultGW(msg),
    onSubmit: makeFormikOnSubmit(submitHostToDefaultGW),
  });

  useEffect(() => {
    if (ctxProps.pathLookUpMetadata?.parameters.pathLookupType === 'unicast') {
      setUnicastInitialValues(
        unicastParametersToFormValues(ctxProps.pathLookUpMetadata?.parameters),
      );
    } else if (
      ctxProps.pathLookUpMetadata?.parameters.pathLookupType === 'multicast'
    ) {
      setMulticastInitialValues(
        multicastParametersToFormValues(ctxProps.pathLookUpMetadata.parameters),
      );
    } else if (
      ctxProps.pathLookUpMetadata?.parameters.pathLookupType ===
      'hostToDefaultGW'
    ) {
      setHostTODefaultGWInitialValues(
        hostToDefaultGWParametersToFormValues(
          ctxProps.pathLookUpMetadata.parameters,
        ),
      );
    }
  }, [ctxProps.pathLookUpMetadata?.parameters]);

  const resetAdvancedOptionsToSelectedPreset = () => {
    const commonValues = {
      protocol: ctxProps.selectedAdvancedOptions.protocol,
      ttl: ctxProps.selectedAdvancedOptions.ttl,
      fragmentOffset: ctxProps.selectedAdvancedOptions.fragmentOffset,
      srcRegions: ctxProps.selectedAdvancedOptions.srcRegions,
      dstRegions: ctxProps.selectedAdvancedOptions.dstRegions,
      applications: ctxProps.selectedAdvancedOptions.otherOptions.applications,
      applicationsMatchWithRegex:
        ctxProps.selectedAdvancedOptions.otherOptions
          .applicationsMatchWithRegex ?? true,
      tracked: ctxProps.selectedAdvancedOptions.otherOptions.tracked,
    };

    // we can't just set*InitialValues because they will be likely the same
    // when reset button is pressed.
    unicastFormik.resetForm({
      values: {
        ...unicastFormik.values,
        ...commonValues,
        ...advancedOptionsToFormValues(ctxProps.selectedAdvancedOptions),
      },
    });

    multicastFormik.resetForm({
      values: {
        ...multicastFormik.values,
        ...commonValues,
        ...advancedOptionsToFormValues(ctxProps.selectedAdvancedOptions),
      },
    });

    setAppliedAdvancedOptionsPresetId(ctxProps.selectedAdvancedOptions.id);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: run only on selection change
  useEffect(() => {
    if (
      ctxProps.selectedAdvancedOptions.id === appliedAdvancedOptionsPresetId
    ) {
      return;
    }

    resetAdvancedOptionsToSelectedPreset();
  }, [ctxProps.selectedAdvancedOptions]);

  return {
    resetAdvancedOptionsToSelectedPreset,
    hostToDefaultGWFormik,
    multicastFormik,
    unicastFormik,
  };
};
